// @flow
import React, { Component } from 'react';

import type { AbstractBlock } from '#common/flow-types/blocks';
import type { IRenderMode } from '#common/flow-types/build';
import type { Sku } from '#common/flow-types/sku';
import type { StoreItemType } from '#common/flow-types/store';
import type { ThemeType } from '#common/model/landing';
import type { Subscription } from '#common/model/subscription';
import type { FiveSymbolLocale } from '#common/utils/locale';

import { addScriptData } from '../../../reducers/modules/site-builder/scripts';
import clearTags from '../../../utils/clear-tags';
import AffiliateBanner from '../AffiliateBanner';
import CustomButton from '../CustomButton';
import LightBox from '../LightBox';
import Platforms from '../Platforms';
import NewsSubscription from '../Subscription';

import { RenderMode } from '#common/flow-types/build';

import './style.pcss';

type Props = {|
  block: AbstractBlock,
  callToAction: Object,
  index: number,
  landingTheme: ThemeType,
  locale: FiveSymbolLocale,
  renderMode: IRenderMode,
  isServerSideRender: boolean,
  onClick: () => void,
  onBlur: () => void,
  addScriptData: typeof addScriptData,
  buttonId: string,
  cartId: string,
  skus: Sku[],
  bundles: StoreItemType[],
  isStoreDataLoading: boolean,
  subscriptionPlans: Subscription[],
  isSubscriptionLoading: boolean,
  merchantId: string,
  projectId: string,
  analyticsCounterId: number,
  isNewScheme: boolean,
  isMultipage: boolean,
  enableCart: boolean,
|}

export type CustomButtonStyles = {|
  color: string,
  backgroundImage: string,
  backgroundSize: string,
  '--bg-color': string
|};

class CallToAction extends Component<Props> {
  static defaultProps = {
    cartId: '',
    renderMode: RenderMode.PUBLICATION
  }

  get customStyles(): CustomButtonStyles {
    const { landingTheme, callToAction } = this.props;
    const { background: buttonBackground, textColor } = callToAction;
    const { accentColor, buttonTextColor } = landingTheme;

    // TODO move to css-vars theme
    return {
      color: buttonBackground && buttonBackground.enable && textColor ? textColor : buttonTextColor,
      backgroundImage: buttonBackground && buttonBackground.enable && buttonBackground.img ? `url(${buttonBackground.img})` : 'none',
      backgroundSize: buttonBackground && buttonBackground.size ? buttonBackground.size : 'contain',
      '--bg-color': buttonBackground && buttonBackground.enable && buttonBackground.color ? buttonBackground.color : accentColor
    };
  }

  handleChangeText = ({ target }: Object) => {
    const { callToAction, locale } = this.props;
    callToAction.label = {
      ...callToAction.label,
      [locale]: clearTags(target.value)
    };
  };

  handleBlur = () => {
    const { onBlur } = this.props;
    if (onBlur) {
      onBlur();
    }
  };

  renderBlockComponent = () => {
    const {
      block, callToAction, isServerSideRender,
      renderMode, onClick, addScriptData, buttonId,
      locale, landingTheme, skus, bundles,
      subscriptionPlans, isStoreDataLoading,
      isSubscriptionLoading, isNewScheme,
      isMultipage, merchantId, projectId, cartId,
      analyticsCounterId,
      enableCart
    } = this.props;

    switch (callToAction.type) {
      case 'platforms': {
        return callToAction.enable && (
          <>
            {block?.values?.affiliate?.enable && (
              <AffiliateBanner
                label={block?.values?.affiliate}
                locale={locale}
              />
            )}
            <Platforms
              callToAction={callToAction}
              locale={locale}
              onBlur={this.handleBlur}
              onCheckoutBlock={onClick}
              isServerSideRender={isServerSideRender}
            />
          </>
        );
      }
      case 'customButton': {
        return callToAction.enable && (
          <div className='swiper-no-swiping'>
            <CustomButton
              button={callToAction}
              blockType={block.module}
              skus={skus}
              bundles={bundles}
              cartId={cartId}
              subscriptionPlans={subscriptionPlans}
              isStoreDataLoading={isStoreDataLoading}
              isSubscriptionLoading={isSubscriptionLoading}
              isNewScheme={isNewScheme}
              isMultipage={isMultipage}
              merchantId={merchantId}
              projectId={projectId}
              analyticsCounterId={analyticsCounterId}
              buttonId={buttonId}
              onChangeText={this.handleChangeText}
              onBlur={this.handleBlur}
              onClick={onClick}
              customStyles={this.customStyles}
              locale={locale}
              renderMode={renderMode}
              isServerSideRender={isServerSideRender}
              addScriptData={addScriptData}
              enableCart={enableCart}
            />
          </div>
        );
      }
      case 'subscribe': {
        return callToAction.enable && (
          <NewsSubscription
            locale={locale}
            callToAction={callToAction}
            landingTheme={landingTheme}
            isServerSideRender={isServerSideRender}
            placeholder={callToAction.label[locale]}
            loginId={callToAction.value}
            blockId={block._id}
            blockType={block.module}
            onCheckoutBlock={onClick}
            onBlur={this.handleBlur}
            isDescriptionEnable={callToAction?.values.text.enable}
            addScriptData={addScriptData}
          />
        );
      }
      case 'lightBox': {
        return callToAction.enable && (
          <LightBox
            callToAction={callToAction}
            blockType={block.module}
            locale={locale}
            isServerSideRender={isServerSideRender}
            onChangeText={this.handleChangeText}
            onBlur={this.handleBlur}
            onClick={onClick}
            theme={this.customStyles}
          />
        );
      }
      default:
        return null;
    }
  };

  render() {
    const { callToAction } = this.props;
    const component = this.renderBlockComponent();
    return component && (
      <div
        className={`ui-site-calltoaction__item ui-site-calltoaction__wrapper--${callToAction.type}`}
        data-buy-button-item
      >
        {component}
      </div>
    );
  }
}

export default CallToAction;
